"use client";

import { type ComponentProps, forwardRef } from "react";

import { cn } from "../lib/utils";

export const TableContainer = forwardRef<HTMLDivElement, ComponentProps<"div">>(
  ({ className, ...props }, ref) => {
    return (
      <div
        ref={ref}
        data-slot="table-container"
        className={cn("relative w-full overflow-x-auto", className)}
        {...props}
      />
    );
  },
);

const Table = ({ className, ...props }: ComponentProps<"table">) => {
  return (
    <table
      data-slot="table"
      className={cn(
        "w-full caption-bottom border-separate border-spacing-0 text-sm",
        className,
      )}
      {...props}
    />
  );
};

const TableHeader = ({ className, ...props }: ComponentProps<"thead">) => {
  return (
    <thead data-slot="table-header" className={cn(className)} {...props} />
  );
};

const TableBody = ({ className, ...props }: ComponentProps<"tbody">) => {
  return (
    <tbody
      data-slot="table-body"
      className={cn("[&_tr:last-child]:border-0", className)}
      {...props}
    />
  );
};

const TableFooter = ({ className, ...props }: ComponentProps<"tfoot">) => {
  return (
    <tfoot
      data-slot="table-footer"
      className={cn(
        "border-t bg-muted/50 font-medium [&>tr]:last:border-b-0",
        className,
      )}
      {...props}
    />
  );
};

const TableRow = ({ className, ...props }: ComponentProps<"tr">) => {
  return (
    <tr
      data-slot="table-row"
      className={cn(
        "flex w-full transition-colors data-[state=selected]:bg-muted",
        className,
      )}
      {...props}
    />
  );
};

const TableHead = ({ className, ...props }: ComponentProps<"th">) => {
  return (
    <th
      data-slot="table-head"
      className={cn(
        "flex h-7 items-center whitespace-nowrap border border-transparent px-2 text-left align-middle font-medium text-muted-foreground text-xs hover:bg-muted/50 [&:has([role=checkbox])]:pr-0",
        className,
      )}
      {...props}
    />
  );
};

const TableCell = ({ className, ...props }: ComponentProps<"td">) => {
  return (
    <td
      data-slot="table-cell"
      className={cn(
        "flex h-7 items-center whitespace-nowrap rounded-sm border border-transparent px-2 align-middle text-xs hover:bg-muted/50 [&:has([role=checkbox])]:pr-0",
        className,
      )}
      {...props}
    />
  );
};

export interface TableStickyFooterProps extends ComponentProps<"div"> {
  /** When true, footer is always visible. When false (default), it auto-hides and reveals on proximity. */
  pinned?: boolean;
  /** Whether the footer is currently visible (controlled by parent proximity logic). Ignored when `pinned` is true. */
  visible?: boolean;
}

/**
 * Footer that lives below the scroll container in a flex column layout.
 * Animates open/closed via CSS grid row transition so it naturally pushes
 * the scroll area smaller — no overlays, no extra scroll space.
 */
const TableStickyFooter = forwardRef<HTMLDivElement, TableStickyFooterProps>(
  ({ className, pinned = false, visible = false, ...props }, ref) => {
    const show = pinned || visible;
    return (
      <div
        className={cn(
          "grid transition-[grid-template-rows] duration-200 ease-out",
          show ? "grid-rows-[1fr] border-border border-t" : "grid-rows-[0fr]",
        )}
      >
        <div
          ref={ref}
          data-slot="table-sticky-footer"
          data-visible={show}
          className={cn("overflow-hidden bg-background", className)}
          {...props}
        />
      </div>
    );
  },
);

const TableCaption = ({ className, ...props }: ComponentProps<"caption">) => {
  return (
    <caption
      data-slot="table-caption"
      className={cn("mt-4 text-muted-foreground text-sm", className)}
      {...props}
    />
  );
};

export {
  Table,
  TableBody,
  TableCaption,
  TableCell,
  TableFooter,
  TableHead,
  TableHeader,
  TableRow,
  TableStickyFooter,
};
